import type {
  ActSourceKind,
  ActSourceWebReference,
  AvailableActCatalogEntry,
  RedactionPlanResponsePayload,
  RedactionProposedPayload,
} from '@hector/shared-types';
import { actSourcePresentation } from '@hector/shared-types';

type WebRedactionSources = NonNullable<RedactionPlanResponsePayload['sources']>;

export interface WebActSourceReadiness {
  canLaunch: boolean;
  /** Blocage qui ne vise AUCUNE règle en particulier (acte indisponible, catalogue absent). */
  reason: string | null;
  /**
   * Blocage propre à chaque règle, pour l'afficher sous SON sélecteur.
   *
   * WHY une table et plus une chaîne unique : la carte routait le message vers
   * le bon bloc en cherchant la sous-chaîne « propre écriture » ou « décision
   * attaquée » dans un texte français. Renommer un libellé métier faisait donc
   * disparaître le message sans erreur de type ni test rouge — et comme
   * l'évaluation s'arrêtait au PREMIER manque, le bloc « écritures adverses »
   * affichait sans filtre le motif d'une règle voisine. Chaque règle porte
   * désormais le sien, et toutes sont évaluées.
   */
  reasonByKind: Partial<Record<ActSourceKind, string>>;
  sources: WebRedactionSources;
}

export interface CanonicalActSelectionState {
  actId: string;
  selectedDocumentIdsByKind: Partial<Record<ActSourceKind, string[]>>;
}

/**
 * Act changes are one synchronous transition so stale source IDs can never
 * coexist for a render with the newly selected catalogue rule.
 */
export function selectCanonicalAct(
  _current: CanonicalActSelectionState,
  nextActId: string,
): CanonicalActSelectionState {
  return { actId: nextActId, selectedDocumentIdsByKind: {} };
}

/**
 * Sélection initiale = la présélection proposée par l'agent, réduite à l'acte
 * qui est effectivement affiché.
 *
 * WHY ce filtre par règle : la carte laisse l'avocat changer d'acte. Une pièce
 * cochée pour une réplique n'a rien à faire sous une assignation, et le serveur
 * n'a validé la présélection que contre l'acte qu'il proposait. Une suggestion
 * qui survivrait au changement d'acte serait envoyée sans avoir jamais été
 * vérifiée pour la nouvelle règle.
 */
export function initialSelectionFromProposal(
  act: AvailableActCatalogEntry | undefined,
  proposed: RedactionProposedPayload['sources'],
): Partial<Record<ActSourceKind, string[]>> {
  if (!act?.sources || !proposed) return {};
  const declared = new Set(act.sources.map((rule) => rule.kind));
  const selection: Partial<Record<ActSourceKind, string[]>> = {};
  for (const entry of proposed) {
    if (!declared.has(entry.kind)) continue;
    const rule = act.sources.find((candidate) => candidate.kind === entry.kind);
    const ids = [...new Set(entry.documentIds)];
    // Un maximum dépassé ne se tronque pas en silence : la règle décide, et
    // proposer plus que ce qu'elle permet ne coche rien plutôt que de choisir
    // arbitrairement lesquelles retenir.
    if (rule && rule.maximum !== null && ids.length > rule.maximum) continue;
    if (ids.length > 0) selection[entry.kind] = ids;
  }
  return selection;
}

/** Converts UI state to choices only; business requirements stay in the catalogue/backend policy. */
export function evaluateWebActSourceReadiness(
  act: AvailableActCatalogEntry | undefined,
  selectedDocumentIdsByKind: Readonly<Partial<Record<ActSourceKind, readonly string[]>>>,
): WebActSourceReadiness {
  const none: WebActSourceReadiness = {
    canLaunch: false,
    reason: null,
    reasonByKind: {},
    sources: [],
  };
  if (!act) return { ...none, reason: 'Choisissez un acte disponible.' };
  if (!act.available) {
    return { ...none, reason: act.unavailableReason ?? 'Cet acte est indisponible.' };
  }
  if (!act.sources) {
    return {
      ...none,
      reason: 'Les règles de sources du catalogue ne sont pas disponibles. Rechargez la page après la mise à jour du service.',
    };
  }

  const publicLaw = act.specialization === 'public';
  const supportedKinds: readonly ActSourceKind[] = [
    'adverse_pleadings',
    'own_prior_pleading',
    'attacked_decision',
  ];
  const unsupported = act.sources.find((rule) =>
    rule.requirement === 'required' && !supportedKinds.includes(rule.kind),
  );
  if (unsupported) {
    const { label } = actSourcePresentation(unsupported.kind, { publicLaw });
    return {
      ...none,
      reason: `La sélection de ${label} sera disponible prochainement pour cet acte.`,
    };
  }

  const sources: WebRedactionSources = [];
  const reasonByKind: Partial<Record<ActSourceKind, string>> = {};
  for (const rule of act.sources) {
    if (!supportedKinds.includes(rule.kind)) continue;
    const references: ActSourceWebReference[] = [...new Set(selectedDocumentIdsByKind[rule.kind] ?? [])]
      .map((documentId) => ({ referenceType: 'case_document' as const, documentId }));
    const { label, singularIndefini } = actSourcePresentation(rule.kind, { publicLaw });

    if (references.length < rule.minimum) {
      // L'article vient du contrat partagé : le genre change d'une source à
      // l'autre, et une phrase assemblée ici serait fausse pour l'une des trois.
      reasonByKind[rule.kind] = rule.maximum === 1
        ? `Sélectionnez ${singularIndefini}.`
        : rule.minimum > 1
          ? `Sélectionnez au moins ${rule.minimum} ${label.toLowerCase()}.`
          : `Sélectionnez au moins ${singularIndefini}.`;
    } else if (rule.maximum !== null && references.length > rule.maximum) {
      reasonByKind[rule.kind] = `${label} : ${rule.maximum} au maximum, ${references.length} sélectionnées.`;
    } else if (references.length > 0) {
      sources.push({ kind: rule.kind, references });
    }
  }

  return {
    canLaunch: Object.keys(reasonByKind).length === 0,
    reason: null,
    reasonByKind,
    sources,
  };
}
